"use client";

import * as React from "react";
import { useState, useEffect, useRef } from "react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Card, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const projects = [{
  id: 1,
  title: "Kitchen Renovation Builder",
  description: "Transform your home with expert kitchen renovation services in London, creating modern, functional spaces tailored to your lifestyle. From design to installation, we deliver high-quality finishes that enhance both comfort and property value.",
  imageBefore: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1586109/img/north_london_kitchen_before_renovation.png",
  imageAfter: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1586109/img/north_london_kitchen_after_modest_luxury.png"
}, {
  id: 2,
  title: "Bathroom Renovation",
  description: "Elevating a standard washroom to a luxury spa-like retreat. Showcasing our meticulous attention to detail and commitment to superior craftsmanship.",
  imageBefore: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1586109/img/bathroom_remodel_before_dated_tiles.png",
  imageAfter: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1586109/img/bathroom_remodel_after_modest_spa.png"
}, {
  id: 3,
  title: "LOFT EXTENSIONS/CONVERSIONS",
  description: "Upgrade your home with professional loft conversions and extensions in London, designed to add space, style, and value. Our expert team handles everything from planning to completion, delivering high-quality results tailored to your needs.",
  imageBefore: "WORK 1",
  imageAfter: "https://solidtrustbuilder.com/wp-content/uploads/2026/03/WhatsApp-Image-2026-03-19-at-11.00.24.jpeg?v=e5umilwf20o"
}];
function ProjectCard({
  project,
  index
}: {
  project: typeof projects[0];
  index: number;
}) {
  const [showAfter, setShowAfter] = useState(true);
  return <div data-index={index} className="h-full pl-4 md:pl-8">
      <Card className="h-full flex flex-col rounded-none border-border bg-card overflow-hidden group">
        <div className="relative aspect-[4/3] md:aspect-[16/9] overflow-hidden bg-muted">
          {}
          <img src={project.imageBefore} alt={`${project.title} Before`} className={cn("absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ease-in-out", showAfter ? "opacity-0" : "opacity-100")} />
          {}
          <img src={project.imageAfter} alt={`${project.title} After`} className={cn("absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ease-in-out", showAfter ? "opacity-100" : "opacity-0")} />
          
          {}
          <div className="absolute top-6 left-6 bg-background/90 backdrop-blur-md px-4 py-2 border border-border shadow-sm">
            <span className="text-xs font-mono uppercase tracking-[0.2em] text-foreground">
              {showAfter ? "After" : "Before"}
            </span>
          </div>
        </div>

        <CardHeader className="border-t border-border p-6 md:p-8 flex-grow flex flex-col justify-between gap-8 bg-card">
          <div className="flex flex-col md:flex-row md:items-start justify-between gap-6">
            <div className="max-w-2xl">
              <CardTitle className="text-2xl md:text-3xl font-light tracking-tight uppercase text-foreground">
                {project.title}
              </CardTitle>
              <CardDescription className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed">
                {project.description}
              </CardDescription>
            </div>
            
            {}
            <div className="flex items-center gap-4 shrink-0 bg-background border border-border px-5 py-3 self-start">
              <span className={cn("text-xs font-mono uppercase tracking-widest transition-colors duration-300", !showAfter ? "text-foreground font-medium" : "text-muted-foreground")}>
                Before
              </span>
              <Switch checked={showAfter} onCheckedChange={setShowAfter} className="data-[state=checked]:bg-foreground data-[state=unchecked]:bg-muted-foreground" />
              <span className={cn("text-xs font-mono uppercase tracking-widest transition-colors duration-300", showAfter ? "text-foreground font-medium" : "text-muted-foreground")}>
                After
              </span>
            </div>
          </div>
        </CardHeader>
      </Card>
    </div>;
}
export default function RefinedPropertyRenovation() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="refined-property-renovation" className="dark bg-background text-foreground py-24 md:py-32 overflow-hidden border-y border-border">
      <div className="container mx-auto px-4 md:px-8 max-w-[100rem]">
        <div ref={headerRef} className={cn("mb-16 md:mb-24 max-w-4xl transition-all duration-1000 ease-out", isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
          <h2 className="text-sm font-mono uppercase tracking-[0.3em] text-muted-foreground mb-6 flex items-center gap-4">
            <span className="w-12 h-px bg-border"></span>
            SolidTrust Builders
          </h2>
          <h3 className="text-4xl md:text-6xl font-light tracking-tighter uppercase leading-[1.1] mb-8">
            Refined Property <br className="hidden md:block" />
            <span className="font-medium">Renovation</span>
          </h3>
          <p className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-2xl">
            As trusted builders in North West London, we specialize in museum-quality interior transformations. Explore our portfolio of meticulous craftsmanship, where every detail is engineered for luxury and longevity.
          </p>
        </div>

        <div className="relative">
          <Carousel opts={{
          align: "start",
          loop: true
        }} className="w-full">
            <CarouselContent className="-ml-4 md:-ml-8">
              {projects.map((project, index) => <CarouselItem key={project.id} className="md:basis-[85%] lg:basis-[75%] xl:basis-[65%]">
                  <ProjectCard project={project} index={index} />
                </CarouselItem>)}
            </CarouselContent>
            
            <div className="flex items-center justify-end gap-4 mt-12 pr-4 md:pr-8">
              <CarouselPrevious className="relative inset-auto translate-x-0 translate-y-0 size-12 rounded-none border-border hover:bg-foreground hover:text-background transition-colors" />
              <CarouselNext className="relative inset-auto translate-x-0 translate-y-0 size-12 rounded-none border-border hover:bg-foreground hover:text-background transition-colors" />
            </div>
          </Carousel>
        </div>
      </div>
    </section>;
}